import { useState } from 'react';
import { IconCross } from '@components/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import classNames from 'classnames';

import { ImageGalleryItem } from '../image-gallery.type';
import { getSortableTransition } from '../utils/get-sortable-transition';

import { ImageGalleryThumbnailImage } from './image-gallery-thumbnail-image';

interface ImageGalleryThumbnailProps {
  image: ImageGalleryItem;
  onDelete: (id: string) => void;
  deleteButtonAriaLabel: string;
  isDeleting?: boolean;
}

export const ImageGalleryThumbnail = ({
  image,
  onDelete,
  deleteButtonAriaLabel,
  isDeleting = false,
}: ImageGalleryThumbnailProps) => {
  // Locally-staged blob URLs (just-picked files held in browser memory)
  // resolve synchronously and `OptimizedImage`'s blob branch doesn't
  // forward `onLoad`. Treat them as already-loaded so the skeleton phase
  // is skipped and the delete X is interactable from the first paint.
  //
  // `isStagedBlob` is intentionally captured at mount. Post-flush the
  // gallery controller swaps the staged id (`staged-N`) for the server
  // id (`srv-…`) and the parent keys thumbnails by `image.id`, so React
  // unmounts the staged row and mounts a fresh one with the CDN URL —
  // the initial value is recalculated then. If that keying contract
  // ever changes, replace this with a sync-on-url effect; today it
  // would just add a redundant re-render.
  const isStagedBlob = image.url.startsWith('blob:');
  const [isLoaded, setIsLoaded] = useState(isStagedBlob);
  const [hasError, setHasError] = useState(false);
  const showSkeleton = !isLoaded && !hasError;

  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: image.id,
    transition: getSortableTransition(),
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`image-gallery__thumbnail-${image.id}`}
      className={classNames('relative w-[60px] h-[60px] shrink-0 transition-transform duration-200 ease-out', {
        'opacity-50 pointer-events-none': isDeleting,
        'opacity-30 pointer-events-none': isDragging,
      })}>
      <div
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        className={classNames('w-full h-full rounded-md outline-none touch-none', {
          'cursor-grab': !isDeleting && !isDragging && isLoaded,
        })}>
        <ImageGalleryThumbnailImage
          image={image}
          isLoaded={isLoaded}
          showSkeleton={showSkeleton}
          onLoad={() => setIsLoaded(true)}
          onError={() => setHasError(true)}
        />
      </div>
      {/* Hide the delete X while the image hasn't loaded yet — clicking it
          on top of an empty shimmer feels off, and the image isn't even
          confirmed to exist on the server until `onLoad` fires. */}
      {isLoaded && (
        <button
          type='button'
          aria-label={deleteButtonAriaLabel}
          data-testid={`image-gallery__delete-${image.id}`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(image.id);
          }}
          className='absolute bottom-[2.5px] right-[3.5px] w-5 h-5 rounded-sm bg-tertiary shadow-sm flex items-center justify-center hover:scale-110 transition-all duration-150'>
          <IconCross variant='s' className='w-3 h-3 text-primary' strokeWidth={2.5} />
        </button>
      )}
    </div>
  );
};
